import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * P10-2 (docs/P10_PHASE1.md §3 items 8/9): a small click-to-toggle popover
 * shell — click-outside and Escape both close it — shared by ProjectList's
 * per-card "…" menu (rename/delete) and Header's構成案スイッチャー (which
 * needs a richer body than a flat action list: a ✓-marked composition list
 * plus its own action rows). `trigger`/`children` are render props so each
 * caller supplies its own content while this owns only the open/close
 * mechanics.
 */
export function Menu({
  trigger,
  children,
  align = 'end',
  testId,
}: {
  trigger: (p: { open: boolean; toggle: () => void }) => ReactNode
  children: (p: { close: () => void }) => ReactNode
  /** Which edge of the trigger the popover panel hangs off. */
  align?: 'start' | 'end'
  testId?: string
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className="relative inline-block" ref={rootRef} data-testid={testId}>
      {trigger({ open, toggle: () => setOpen((v) => !v) })}
      {open && (
        <div
          className={`absolute top-full mt-1 z-50 ${align === 'end' ? 'right-0' : 'left-0'}`}
          data-testid={testId ? `${testId}-panel` : undefined}
        >
          {children({ close: () => setOpen(false) })}
        </div>
      )}
    </div>
  )
}

/** Rounded card wrapper for a Menu's popover body — a flat action list (ProjectList) or custom rich content (Header). */
export function MenuPanel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      role="menu"
      className={`min-w-[180px] rounded bg-ink-surface2 border border-ink-border shadow-glass py-1 ${className}`}
    >
      {children}
    </div>
  )
}

/** One flat action row inside a MenuPanel (e.g. 「名前を変更」「削除」). */
export function MenuItem({
  icon,
  danger,
  disabled,
  title,
  onSelect,
  children,
  testId,
}: {
  icon?: ReactNode
  danger?: boolean
  disabled?: boolean
  /** Tooltip — e.g. why the item is disabled (task spec §3-9: 「進行中jobがあれば削除をdisabledにし理由をツールチップ」). */
  title?: string
  onSelect: () => void
  children: ReactNode
  testId?: string
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      title={title}
      onClick={onSelect}
      data-testid={testId}
      className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent ${
        danger ? 'text-accent-red' : 'text-text'
      }`}
    >
      {icon}
      <span className="truncate">{children}</span>
    </button>
  )
}
